"use client";

import { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { signIn, useSession } from "next-auth/react";

function buildCallbackUrlWithoutLoginParam({ pathname, searchParams }) {
  const params = new URLSearchParams(searchParams?.toString?.() ?? "");
  params.delete("login");

  const query = params.toString();
  const hash = typeof window !== "undefined" ? window.location.hash : "";
  const origin = typeof window !== "undefined" ? window.location.origin : "";

  return `${origin}${pathname}${query ? `?${query}` : ""}${hash}`;
}

function DelegatedAuthDeepLinkInner() {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { status } = useSession();

  const [isMaintenance, setIsMaintenance] = useState(false);
  const hasTriggeredRef = useRef(false);
  const hasCleanedUrlRef = useRef(false);

  const hasLoginIntent = useMemo(() => {
    const login = searchParams?.get("login");
    return (login ?? "").toLowerCase() === "true";
  }, [searchParams]);

  const callbackUrl = useMemo(() => {
    if (!hasLoginIntent) return null;
    return buildCallbackUrlWithoutLoginParam({ pathname, searchParams });
  }, [hasLoginIntent, pathname, searchParams]);

  useEffect(() => {
    if (!hasLoginIntent) return;

    fetch("/api/maintenance")
      .then((r) => r.json())
      .then((d) => setIsMaintenance(!!d.mode))
      .catch(() => setIsMaintenance(false));
  }, [hasLoginIntent]);

  useEffect(() => {
    if (!hasLoginIntent) return;
    if (!callbackUrl) return;
    if (status === "loading") return;

    if (isMaintenance) {
      router.push("/maintenance");
      return;
    }

    if (status === "unauthenticated" && !hasTriggeredRef.current) {
      hasTriggeredRef.current = true;
      signIn("keycloak", { callbackUrl });
      return;
    }

    if (status === "authenticated" && !hasCleanedUrlRef.current) {
      hasCleanedUrlRef.current = true;
      router.replace(callbackUrl);
    }
  }, [hasLoginIntent, callbackUrl, status, isMaintenance, router]);

  return null;
}

export default function DelegatedAuthDeepLink() {
  // `useSearchParams()` requiere estar bajo un <Suspense> para evitar fallos de prerender/SSG.
  return (
    <Suspense fallback={null}>
      <DelegatedAuthDeepLinkInner />
    </Suspense>
  );
}

